import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
    useTranslate,
    useRecordContext,
} from 'react-admin';
import {
    Grid,
    Box,
    Typography,
    CircularProgress,
    Card,
    CardContent,
    Alert,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    List,
    ListItem,
    ListItemText,
    Divider,
    Stack,
    Button,
} from '@mui/material';

// CRM Field Categories Configuration
const CRM_FIELD_CATEGORIES = [
    {
        categoryName: 'Perustiedot',
        fields: [
            'fullname',
            'emailaddress1',
            'ifa_isoppimisenpalvelutcustomer',
            'ifa_isfinnlecturacustomer',
            'telephone1',
            'mobilephone',
            'jobtitle',
            'department',
            'company',
            'createdon',
            'modifiedon',
            'statecode',
        ]
    },
    {
        categoryName: 'Markkinointikielto',
        fields: [
            'donotphone',
            'donotemail',
            'donotbulkemail',
        ]
    },
    {
        categoryName: 'Osoitetiedot',
        fields: [
            'address1_line1',
            'address1_line2',
            'address1_city',
            'address1_stateorprovince',
            'address1_postalcode',
            'address1_country'
        ]
    },
    {
        categoryName: 'Lisätiedot',
        fields: [
            'managername',
            'assistantname',
            'assistantphone',
            'spousesname',
            'nickname',
            'anniversary'
        ]
    }
];

const CrmContactInfo = () => {
    const record = useRecordContext();
    const [filteredData, setFilteredData] = useState(null);
    const [schoolRelationships, setSchoolRelationships] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [error, setError] = useState(null);
    const translate = useTranslate();

    const teacherGuid = record?.guid;

    // Helper function to get human-readable role type name
    const getRoleTypeName = (roleTypeCode) => {
        if (!roleTypeCode) return null;
        const roleTypeStr = String(roleTypeCode);
        const roleName = translate(`resources.teacher.crm.roleTypes.${roleTypeStr}`);
        // If translation key doesn't exist, return the code
        return roleName.startsWith('resources.teacher.crm.roleTypes.') ? roleTypeStr : roleName;
    };

    // Category-based filter function that preserves field order
    const categorizeCrmData = useCallback((rawData) => {
        if (!rawData) return null;

        const categorizedData = [];
        
        // Process categories in their defined order
        CRM_FIELD_CATEGORIES.forEach(category => {
            const categoryFields = [];
            
            // Process fields in their defined order within each category
            category.fields.forEach(fieldName => {
                if (rawData.hasOwnProperty(fieldName)) {
                    const value = rawData[fieldName];
                    // Only include fields that have data (not null, undefined, or empty string)
                    const hasData = value !== null && value !== undefined && value !== '';
                    if (hasData) {
                        categoryFields.push({ key: fieldName, value: value });
                    }
                }
            });
            
            // Only add category if it has fields with data
            if (categoryFields.length > 0) {
                categorizedData.push({
                    categoryName: category.categoryName,
                    fields: categoryFields
                });
            }
        });
        
        return categorizedData;
    }, []);

    // Fetch CRM data function - can be called on mount and after sync
    const fetchCrmData = useCallback(async () => {
        if (!teacherGuid) {
            setError('Teacher GUID not found');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const url = `${process.env.REACT_APP_INFRA_API}/teacher/crm/${teacherGuid}?fields=with_data&include_accounts=true`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const rawCrmData = data.contact_data;
            const categorized = categorizeCrmData(rawCrmData);
            
            setFilteredData(categorized);
            setSchoolRelationships(data.school_relationships || []);
        } catch (err) {
            console.error('Error fetching CRM data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch CRM data');
        } finally {
            setLoading(false);
        }
    }, [teacherGuid, categorizeCrmData]);

    // Handle Frends sync
    const handleFrendsSync = async () => {
        setSyncLoading(true);
        setError(null);

        try {
            // Calculate modifiedOn based on current teacher's modified/created date minus 10 days
            let modifiedOn = '';
            const teacherDate = record?.author?.modified || record?.author?.created;
            if (teacherDate) {
                const date = new Date(teacherDate);
                // Subtract 10 days
                date.setDate(date.getDate() - 10);
                
                // Format as YYYYMMDDHHMMSS000
                modifiedOn = date.getFullYear() + 
                    String(date.getMonth() + 1).padStart(2, '0') + 
                    String(date.getDate()).padStart(2, '0') + 
                    String(date.getHours()).padStart(2, '0') +
                    String(date.getMinutes()).padStart(2, '0') +
                    String(date.getSeconds()).padStart(2, '0') + '000';
            }

            const response = await fetch(`${process.env.REACT_APP_INFRA_API}/frends/P635`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
                body: JSON.stringify({
                    modifiedOn: modifiedOn,
                    query: teacherGuid,
                    batchSize: 1
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Frends sync response:', data);

            // Refresh CRM data after successful sync
            await fetchCrmData();
        } catch (err) {
            console.error('Error syncing with Frends:', err);
            setError(err instanceof Error ? err.message : 'Failed to sync with Frends');
        } finally {
            setSyncLoading(false);
        }
    };

    useEffect(() => {
        fetchCrmData();
    }, [fetchCrmData]);

    // Helper function to render field value
    const renderFieldValue = (key, value) => {
        if (value === null || value === undefined || value === '') {
            return translate('resources.teacher.crm.not_available');
        }
        
        // Handle statecode with translation and color
        if (key === 'statecode') {
            const stateCodeStr = String(value);
            const translated = translate(`resources.teacher.crm.statecode.${stateCodeStr}`);
            const text = translated.startsWith('resources.teacher.crm.statecode.') ? stateCodeStr : translated;
            const color = value === 0 ? 'green' : value === 1 ? 'red' : 'inherit';
            return <Typography component="span" sx={{ color }}>{text}</Typography>;
        }
        
        // Handle different data types
        if (typeof value === 'boolean') {
            return value ? translate('resources.teacher.crm.yes') : translate('resources.teacher.crm.no');
        }
        
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        
        // Handle dates with Finnish format (dd.MM.yyyy)
        if (key.includes('date') || key.includes('on')) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString('fi-FI', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });
                }
            } catch (e) {
                // Not a valid date, return as string
            }
        }
        
        return String(value);
    };

    // Get human-readable field names using translations
    const getFieldLabel = (fieldName) => {
        const translationKey = `resources.teacher.crm.fieldLabels.${fieldName}`;
        const translated = translate(translationKey);
        
        // If translation exists (not same as key), use it; otherwise format the field name
        return translated !== translationKey 
            ? translated 
            : fieldName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    if (!teacherGuid) {
        return (
            <Card>
                <CardContent>
                    <Alert severity="info">
                        {translate('resources.teacher.crm.no_teacher_guid')}
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            {translate('resources.teacher.crm.loading')}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        const is404Error = error.includes('404') || error.toLowerCase().includes('not found');
        
        return (
            <>
                {is404Error && (
                    <Box mb={2} display="flex" justifyContent="flex-end">
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleFrendsSync}
                            disabled={syncLoading}
                            startIcon={syncLoading ? <CircularProgress size={20} /> : null}
                        >
                            {syncLoading ? 'Synkronoidaan...' : 'Synkronoi CRM (Frends)'}
                        </Button>
                    </Box>
                )}
                <Card>
                    <CardContent>
                        {is404Error ? (
                            <Alert severity="warning">
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                    CRM-kontaktia ei löytynyt
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    Tätä opettajaa ei ole vielä luotu CRM-järjestelmään.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Voit luoda kontaktin klikkaamalla yllä olevaa <strong>"Synkronoi CRM (Frends)"</strong> -painiketta. 
                                    Synkronointi käynnistää prosessin ja luo kontaktin, jos sitä ei vielä ole olemassa.
                                </Typography>
                            </Alert>
                        ) : (
                            <Alert severity="error">
                                <Typography variant="body2">
                                    {translate('resources.teacher.crm.error')}: {error}
                                </Typography>
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            </>
        );
    }

    return (
        <>
            {/* Frends Sync Button */}
            <Box mb={2} display="flex" justifyContent="flex-end">
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleFrendsSync}
                    disabled={syncLoading}
                    startIcon={syncLoading ? <CircularProgress size={20} /> : null}
                >
                    {syncLoading ? 'Synkronoidaan...' : 'Synkronoi CRM (Frends)'}
                </Button>
            </Box>
            
            <Grid container spacing={2}>
            {/* Left Side - Contact Information */}
            <Grid size={{ xs: 12, md: 5 }}>
                <Box boxShadow={3} p={2}>
                    <Box mb={2} boxShadow={1} p={2} bgcolor="background.paper">
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                            {translate('resources.teacher.crm.contact_info')}
                        </Typography>
                        
                        {filteredData ? (
                            <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
                                {filteredData.map((category, categoryIndex) => (
                                    <Box key={category.categoryName}>
                                        {categoryIndex > 0 && (
                                            <Divider component="li" sx={{ margin: '16px 0', opacity: 0.6 }} />
                                        )}
                                        <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', px: 0 }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ 
                                                fontWeight: 600,
                                                mb: 2,
                                                width: '100%'
                                            }}>
                                                {category.categoryName}
                                            </Typography>
                                            
                                            <ListItemText
                                                sx={{ m: 0, width: '100%' }}
                                                primary={
                                                    <Stack direction="column" spacing={1.5}>
                                                        {category.fields.map((field) => (
                                                            <Stack
                                                                key={field.key}
                                                                direction="row"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                sx={{ width: '100%' }}
                                                            >
                                                                <Typography component="span" variant="body2" sx={{ opacity: 0.7 }}>
                                                                    {getFieldLabel(field.key)}
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        flexGrow: 1,
                                                                        height: '1px',
                                                                        backgroundImage: 'linear-gradient(to right, transparent, black 50%, transparent)',
                                                                        backgroundSize: '4px 1px',
                                                                        margin: '0 8px',
                                                                    }}
                                                                />
                                                                <Box sx={{ opacity: 1, marginLeft: 'auto', textAlign: 'right', maxWidth: '60%' }}>
                                                                    <Typography variant="body2">
                                                                        {renderFieldValue(field.key, field.value)}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        ))}
                                                    </Stack>
                                                }
                                            />
                                        </ListItem>
                                    </Box>
                                ))}
                            </List>
                        ) : (
                            <Alert severity="info">
                                {translate('resources.teacher.crm.no_data')}
                            </Alert>
                        )}
                    </Box>
                </Box>
            </Grid>
            
            {/* Right Side - School Relationships */}
            <Grid size={{ xs: 12, md: 7 }}>
                <Box boxShadow={3} p={2}>
                    <Box boxShadow={1} p={2} bgcolor="background.paper">
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                            {translate('resources.teacher.crm.school_relationships_title')} 
                            {schoolRelationships && schoolRelationships.length > 0 && ` (${schoolRelationships.length})`}
                        </Typography>
                        
                        {schoolRelationships && schoolRelationships.length > 0 ? (
                            <Table size="small" sx={{ 
                                '& .MuiTableCell-root': { 
                                    borderBottom: '1px solid #e0e0e0',
                                    py: 1.5
                                }
                            }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {translate('resources.teacher.crm.table_headers.school_name')}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {translate('resources.teacher.crm.table_headers.role')}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {translate('resources.teacher.crm.table_headers.subjects')}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {translate('resources.teacher.crm.table_headers.status')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {schoolRelationships.map((rel, index) => {
                                        const account = rel.account;
                                        const relationship = rel.relationship;
                                        
                                        if (!account) {
                                            return (
                                                <TableRow key={index}>
                                                    <TableCell colSpan={3}>
                                                        <Alert severity="warning" sx={{ py: 0.5 }}>
                                                            {translate('resources.teacher.crm.account_not_found')}
                                                            {rel.error && `: ${rel.error}`}
                                                        </Alert>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }
                                        
                                        return (
                                            <TableRow 
                                                key={index}
                                                sx={{ 
                                                    '&:hover': { backgroundColor: '#f9f9f9' },
                                                    '&:last-child td': { borderBottom: 0 }
                                                }}
                                            >
                                                {/* School Name Column */}
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {account.name || translate('resources.teacher.crm.unnamed_account')}
                                                        </Typography>
                                                        {relationship.createdon && (
                                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                {new Date(relationship.createdon).toLocaleDateString('fi-FI', {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: 'numeric'
                                                                })}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                
                                                {/* Role Column */}
                                                <TableCell>
                                                    {relationship.ifa_roletype && (
                                                        <Typography variant="body2">
                                                            {getRoleTypeName(relationship.ifa_roletype)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                
                                                {/* Subjects Column */}
                                                <TableCell>
                                                    {relationship.subjects && relationship.subjects.length > 0 ? (
                                                        <Typography variant="body2">
                                                            {relationship.subjects
                                                                .map(subject => subject.ifa_abbreviation || subject.ifa_code)
                                                                .join(', ')
                                                            }
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                            -
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                
                                                {/* Status Column */}
                                                <TableCell>
                                                    {relationship.statecode !== undefined && relationship.statecode !== null ? (
                                                        <Typography variant="body2">
                                                            {renderFieldValue('statecode', relationship.statecode)}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                            -
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        ) : (
                            <Alert severity="info">
                                {translate('resources.teacher.crm.no_schools')}
                            </Alert>
                        )}
                    </Box>
                </Box>
            </Grid>
        </Grid>
        </>
    );
};

export default CrmContactInfo;
